import React, { useState } from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Modal,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers';
import { useFormikContext } from 'formik';
import {
  CustomPersonalDetailData,
  CustomPersonalDetailTypes,
  HolidayCalendar,
  OrderPageData,
} from '../../../../../api';
import { RenderMultilineDetail } from './personalDetails/RenderMultilineDetail';
import { RenderBooleanDetail } from './personalDetails/RenderBooleanDetail';
import { FormStyledMenuItem, RenderDropDownDetail } from './personalDetails/RenderDropDownDetail';
import PluginUtils, { OrderCustomDetails, PluginType } from '../../../../../common/PluginUtils';
import { OrderPageIntent } from '../../OrderPageIntent';
import { DATE_FORMAT } from '../../../../consts';
import { EditOrderFormValues } from '../../EditOrderDialog';

export type { EditOrderFormValues as PersonalDetailsValues };

type PersonalDetailsStepProps = {
  orderPage: any;
  setOrderCustomDetails?: (customDetails: OrderCustomDetails) => void;
  orderPageIntent?: OrderPageIntent;
  setPreviewName?: (reviewName: string) => void;
  orderCustomDetails?: any;
  holidayCalendar?: HolidayCalendar;
  showReferralSource: boolean;
  renderPhoneField?: () => React.ReactNode;
};

const PersonalDetailsStep = ({
  orderPage,
  setOrderCustomDetails,
  orderPageIntent,
  setPreviewName,
  orderCustomDetails,
  holidayCalendar,
  showReferralSource,
  renderPhoneField,
}: PersonalDetailsStepProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isGiftCardIntent = orderPageIntent === OrderPageIntent.GIFT_CARD;

  const [weekendAndIsraeliHolidaysChecked, setWeekendAndIsraeliHolidaysChecked] = useState(
    orderCustomDetails?.[PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS] === true,
  );
  const [isDatePickerOpen, setIsDatePickerOpen] = React.useState(false);

  const { values, errors, touched, handleChange, setFieldValue } = useFormikContext<EditOrderFormValues>();

  const handleWeekendCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWeekendAndIsraeliHolidaysChecked(event.target.checked);
    if (setOrderCustomDetails) {
      setOrderCustomDetails({ [PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS]: event.target.checked });
    }
  };
  const holidaysPlugin = PluginUtils.getPluginData(orderPage.plugins, PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS);
  const percentString = t('order_page.weekend_checkbox', {
    percent: holidaysPlugin ? ((holidaysPlugin.multiply - 1) * 100.0).toFixed(0) : 0,
  });

  return (
    <div className="personal-details-step">
      {isGiftCardIntent && holidaysPlugin && (
        <Grid container flexDirection="column">
          <Typography variant="h6">{t('order_page.extras')}</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={weekendAndIsraeliHolidaysChecked}
                onChange={handleWeekendCheckboxChange}
                sx={{
                  color: theme.customTheme.palette.border,
                  '&.Mui-checked': {
                    color: theme.customTheme.palette.border,
                  },
                }}
              />
            }
            label={percentString}
          />
        </Grid>
      )}
      <Grid
        gap={1.5}
        container
        flexDirection="column"
        border={isGiftCardIntent ? '1px solid' : undefined}
        borderColor="divider"
        borderRadius={isGiftCardIntent ? '8px' : undefined}
        p={isGiftCardIntent ? 2 : undefined}
        mt={isGiftCardIntent ? 1.5 : undefined}>
        {isGiftCardIntent && <Typography variant="h6">{t('order_page.gifter_personal_details')}</Typography>}
        {renderPhoneField?.()}
        <TextField
          fullWidth
          className="text-field"
          id="firstname"
          name="firstname"
          label={t('order_page.firstname')}
          value={values.firstname}
          onChange={(e) => {
            if (setPreviewName) {
              setPreviewName(`${e.target.value} ${values.lastname}`);
            }
            handleChange(e);
          }}
          error={touched.firstname && Boolean(errors.firstname)}
          helperText={touched.firstname && errors.firstname}
        />
        <TextField
          fullWidth
          className="text-field"
          id="lastname"
          name="lastname"
          label={t('order_page.lastname')}
          value={values.lastname}
          onChange={(e) => {
            if (setPreviewName) {
              setPreviewName(`${values.firstname} ${e.target.value}`);
            }
            handleChange(e);
          }}
          error={touched.lastname && Boolean(errors.lastname)}
          helperText={touched.lastname && errors.lastname}
        />
        <TextField
          fullWidth
          className="text-field"
          id="alternative_full_name"
          name="alternative_full_name"
          label={t('order_page.alternative_full_name')}
          value={values.alternative_full_name}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          className="text-field"
          id="email"
          name="email"
          label={t('order_page.email')}
          value={values.email}
          onChange={(e: any) => {
            e.target.value = e.target.value.trim();
            handleChange(e);
          }}
          error={touched.email && Boolean(errors.email)}
          helperText={touched.email && errors.email}
        />
        {!renderPhoneField && (
          <TextField
            fullWidth
            className="text-field"
            id="phone"
            name="phone"
            label={t('order_page.phone')}
            type="phone"
            value={values.phone}
            onChange={handleChange}
            error={touched.phone && Boolean(errors.phone)}
            helperText={touched.phone && errors.phone}
          />
        )}

        {orderPage.custom_personal_details?.length > 0 &&
          !isGiftCardIntent &&
          (orderPage as OrderPageData).custom_personal_details?.map(
            (custom_personal_detail: CustomPersonalDetailData) => {
              if (custom_personal_detail.type === CustomPersonalDetailTypes.MULTILINE) {
                return (
                  <RenderMultilineDetail
                    key={custom_personal_detail.id}
                    customPersonalDetail={custom_personal_detail}
                    handleChange={handleChange}
                    values={values}
                    touched={touched}
                    errors={errors}
                  />
                );
              }
              if (custom_personal_detail.type === CustomPersonalDetailTypes.BOOLEAN) {
                return (
                  <RenderBooleanDetail
                    customPersonalDetail={custom_personal_detail}
                    setFieldValue={setFieldValue}
                    values={values}
                    touched={touched}
                    errors={errors}
                  />
                );
              }

              if (custom_personal_detail.type === CustomPersonalDetailTypes.DROP_DOWN) {
                return (
                  <RenderDropDownDetail
                    customPersonalDetail={custom_personal_detail}
                    setFieldValue={setFieldValue}
                    values={values}
                  />
                );
              }
              return <div />;
            },
          )}
      </Grid>
      {isGiftCardIntent && (
        <Grid
          container
          flexDirection="column"
          border="1px solid"
          borderColor="divider"
          borderRadius="8px"
          p={2}
          mt={1.5}>
          <TextField
            multiline
            fullWidth
            className="text-field"
            id="voucher_greeting_message"
            name="voucher_greeting_message"
            label={t('order_page.voucher_card_message')}
            value={values.voucher_greeting_message}
            onChange={handleChange}
            error={touched.voucher_greeting_message && Boolean(errors.voucher_greeting_message)}
            helperText={touched.voucher_greeting_message && errors.voucher_greeting_message}
          />
          <FormControlLabel
            control={
              <Checkbox
                id="should_send_to_recipient"
                name="should_send_to_recipient"
                checked={values.should_send_to_recipient}
                onChange={() => setFieldValue('should_send_to_recipient', !values.should_send_to_recipient)}
              />
            }
            label={t('order_page.should_send_to_recipient')}
          />
          {values.should_send_to_recipient && (
            <Grid mt={-1}>
              <TextField
                fullWidth
                className="text-field"
                id="recipient_email"
                name="recipient_email"
                label={t('order_page.recipient_email')}
                value={values.recipient_email}
                onChange={handleChange}
                error={touched.recipient_email && Boolean(errors.recipient_email)}
                helperText={touched.recipient_email && errors.recipient_email}
              />
              <Grid container flexDirection="column" mt={2}>
                <ToggleButtonGroup
                  exclusive
                  value={values.should_send_delayed ? 'scheduled' : 'immediate'}
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      const isScheduled = newValue === 'scheduled';
                      void setFieldValue('should_send_delayed', isScheduled);
                      void setFieldValue('delivery_date', moment().format(DATE_FORMAT));
                      void setFieldValue('delivery_time', '15:00');
                      if (!isScheduled) {
                        void setFieldValue('delivery_date', null);
                        void setFieldValue('delivery_time', null);
                      }
                    }
                  }}>
                  <ToggleButton value="immediate" className="toggle-button">
                    {t('order_page.send_immediately')}
                  </ToggleButton>
                  <ToggleButton value="scheduled" className="toggle-button">
                    {t('order_page.send_scheduled')}
                  </ToggleButton>
                </ToggleButtonGroup>

                {values.should_send_delayed && (
                  <Grid container spacing={2} mt={2}>
                    <Grid item xs={6} md={4} gap={2}>
                      <TextField
                        fullWidth
                        className="text-field"
                        id="delivery_date"
                        name="delivery_date"
                        label={t('order_page.deliveryDate')}
                        value={
                          values.delivery_date
                            ? moment(values.delivery_date)
                                .locale(holidayCalendar === HolidayCalendar.US ? 'en-US' : 'en-GB')
                                .format(holidayCalendar === HolidayCalendar.US ? 'MM/DD/YYYY' : 'DD/MM/YYYY')
                            : ''
                        }
                        onClick={() => setIsDatePickerOpen(true)}
                        InputProps={{
                          readOnly: true,
                        }}
                        error={touched.delivery_date && Boolean(errors.delivery_date)}
                        helperText={touched.delivery_date && errors.delivery_date}
                        InputLabelProps={{ shrink: true }}
                      />
                      <Modal
                        open={isDatePickerOpen}
                        onClose={() => setIsDatePickerOpen(false)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                        <DatePicker
                          referenceDate={moment()}
                          openTo="day"
                          disablePast
                          value={values.delivery_date ? moment(values.delivery_date) : null}
                          minDate={moment()}
                          maxDate={moment().add(6, 'months')}
                          slots={{}}
                          onChange={(date: any) => {
                            void setFieldValue('delivery_date', date?.format(DATE_FORMAT));
                            setIsDatePickerOpen(false);
                          }}
                          shouldDisableDate={() => false}
                        />
                      </Modal>
                    </Grid>

                    <Grid item xs={6} md={4}>
                      <FormControl fullWidth>
                        <InputLabel id="delivery-time-select">{t('order_page.deliveryTime')}</InputLabel>
                        <Select
                          labelId="delivery-time-select"
                          label={t('order_page.deliveryTime')}
                          id="delivery_time"
                          name="delivery_time"
                          value={values.delivery_time}
                          onChange={(e) => {
                            void setFieldValue('delivery_time', e.target.value);
                          }}>
                          {['09', '12', '15', '18', '21', '00'].map((hour) => {
                            const timeDisplay =
                              holidayCalendar === HolidayCalendar.US
                                ? moment(hour, 'HH').format('hh:mm A')
                                : `${hour}:00`;
                            return (
                              <FormStyledMenuItem
                                key={`hour-${hour}`}
                                value={`${hour}:00`}
                                textColor={theme.customTheme.palette.text.primary}
                                backgroundColor={theme.customTheme.palette.background}>
                                {timeDisplay}
                              </FormStyledMenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
      {showReferralSource && values.referral_source && (
        <Grid container flexDirection="column" item mt={1.5}>
          <Typography variant="caption" color="text.secondary">
            {t('calendar.edit_order.referralSource')}
          </Typography>
          <Typography>{t(`order_page.refferOptions.${values.referral_source}`)}</Typography>
        </Grid>
      )}
    </div>
  );
};

export default PersonalDetailsStep;
